<svelte:options runes={false} />
<script>
  // Execution tab (TASK-20260728-researcher-observability-ui, §10 realized-vs-modeled): execution quality
  // for every terminal order — slippage against the modeled-cost assumption (one of the main things paper
  // trading exists to measure), decided→placed→resolved latency percentiles, fee accumulation, status
  // distribution, and a per-symbol rollup. Aggregates are SERVER-computed (identical numbers everywhere);
  // this tab shapes and renders them. Stats are null (rendered as —) when no computable sample exists —
  // honest absence, never a fake zero. Live wiring mirrors CandlesSignalsTab (NAV-tick hint + 30 s poll).
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { navTickSeq, decisionTickSeq } from '../../../lib/autotradeStore.js';
  import { formatBps, formatNum } from '../../../lib/lineage.js';
  import { modeledBps as modeledBpsOf, slippagePoints, slippageHistogram, perSymbolStats } from '../../../lib/executionQuality.js';
  import SlippageChart from '../../shared/charts/SlippageChart.svelte';

  export let strategy = null;
  export let status = null;

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  let quality = null; // ExecutionQualityResult { rows, stats }
  let version = null;
  let loading = false;
  let error = null;
  let inFlight = false;

  async function loadAll() {
    if (!strategyId) return;
    if (inFlight) return;
    inFlight = true;
    loading = true;
    const at = $selectedOrgId;
    const sid = strategyId;
    try {
      const [q, v] = await Promise.all([
        api.getExecutionQuality(sid, {}, at),
        api.getLineageVersion(sid, at).catch(() => null),
      ]);
      if ($selectedOrgId !== at || strategy?.strategyId !== sid) return;
      quality = q;
      version = v;
      error = null;
    } catch (err) {
      const msg = String(err?.message ?? err);
      error = msg.includes('status: 404')
        ? null
        : (msg.includes('details: ') ? msg.slice(msg.indexOf('details: ') + 'details: '.length) : msg);
    } finally {
      loading = false;
      inFlight = false;
    }
  }

  // (Strategy, org) reload — guard seeded from raw stores, not `$:` values (lesson 2026-07-14).
  let lastKey = '';
  $: {
    const key = `${strategy?.strategyId ?? ''}|${$selectedOrgId ?? ''}`;
    if (key !== lastKey && strategy?.strategyId) {
      lastKey = key;
      quality = null;
      loadAll();
    }
  }

  let lastSeq = 0;
  $: if (isRunning && $navTickSeq !== lastSeq) {
    lastSeq = $navTickSeq;
    loadAll();
  }

  // Refetch on a REAL decision watermark too (this task) — precise over the NAV cadence; both coalesce in
  // loadAll's inFlight guard. Seeded from a raw var, not a `$:` value (lesson 2026-07-14).
  let lastDecisionSeq = 0;
  $: if (isRunning && $decisionTickSeq !== lastDecisionSeq) {
    lastDecisionSeq = $decisionTickSeq;
    loadAll();
  }

  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(loadAll, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });
  onMount(loadAll);

  $: rows = quality?.rows ?? [];
  $: stats = quality?.stats ?? null;
  $: modeled = modeledBpsOf(version);
  $: points = slippagePoints(rows);
  $: histogram = slippageHistogram(points.map((p) => p.value));
  $: maxBucket = Math.max(1, ...histogram.map((b) => b.count));
  $: symbolStats = perSymbolStats(rows);
  $: statusEntries = stats ? Object.entries(stats.byStatus ?? {}).sort((a, b) => b[1] - a[1]) : [];

  function fmtMs(v) {
    if (v === null || v === undefined) return '—';
    return v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`;
  }
  function fmtTime(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  // §10 monthly report CSV (TASK-20260728-monthly-export-decision-sse): one click downloads the server-built
  // report (daily net/gross return, turnover, holdings, regime + a window summary with realized-vs-modeled
  // slippage, hit rate, exits-by-rule). Scoped to the selected org like every read. No window passed → the
  // full run; the server omits gap days honestly. Disabled with honest copy when the run has no outcomes.
  let reportBusy = false;
  let reportError = null;
  async function onDownloadReport() {
    if (reportBusy || !strategyId) return;
    reportBusy = true;
    reportError = null;
    try {
      const { blob, filename } = await api.exportMonthlyReport(strategyId, {}, $selectedOrgId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Deferred revoke (Safari cancels downloads whose blob URL is revoked synchronously — mirrors the ZIP export).
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      console.error('Monthly report download failed:', err);
      reportError = 'Report download failed — check your access to the selected org and try again.';
    } finally {
      reportBusy = false;
    }
  }
</script>

<div class="ex-root">
  <div class="ex-toolbar">
    {#if isRunning}<span class="ex-live" title="Updating live on each NAV tick">● live</span>{/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll} disabled={loading}>Refresh</button>
    <button
      type="button"
      class="btn btn-ghost btn-sm ex-report-btn"
      on:click={onDownloadReport}
      disabled={reportBusy || loading}
      title="Download the §10 monthly report (daily returns, realized-vs-modeled cost, exits by rule) as CSV"
    >{reportBusy ? 'Preparing…' : 'Download report (CSV)'}</button>
  </div>
  {#if reportError}<div class="alert alert-danger" role="alert">{reportError}</div>{/if}

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadAll}>Retry</button>
    </div>
  {:else if loading && !quality}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if !stats || stats.count === 0}
    <div class="empty-state">
      <p class="empty-state-description">
        No terminal orders recorded for this run{$selectedOrgId ? ' in the selected org' : ''} yet.
        {#if isRunning}Execution outcomes appear here as orders resolve.{/if}
      </p>
    </div>
  {:else}
    <!-- Stat tiles: the headline quality numbers. -->
    <div class="ex-tiles">
      <div class="ex-tile">
        <span class="ex-tile-label">Orders (terminal)</span>
        <span class="ex-tile-value">{stats.count}</span>
        <span class="ex-tile-sub">
          {#each statusEntries as [s, n], i}{i > 0 ? ' · ' : ''}{s} {n}{/each}
        </span>
      </div>
      <div class="ex-tile">
        <span class="ex-tile-label">Slippage mean / p50 / p95</span>
        <span class="ex-tile-value">{formatBps(stats.meanSlippageBps)}</span>
        <span class="ex-tile-sub num">p50 {formatBps(stats.p50SlippageBps)} · p95 {formatBps(stats.p95SlippageBps)} · n={stats.slippageSamples}</span>
      </div>
      <div class="ex-tile" class:ex-tile-warn={stats.meanSlippageBps != null && stats.meanSlippageBps > modeled}>
        <span class="ex-tile-label">vs modeled {modeled} bps</span>
        <span class="ex-tile-value">
          {#if stats.meanSlippageBps == null}—{:else}{stats.meanSlippageBps > modeled ? 'ABOVE model' : 'within model'}{/if}
        </span>
        <span class="ex-tile-sub">realized-vs-modeled is a §10 headline measure</span>
      </div>
      <div class="ex-tile">
        <span class="ex-tile-label">Latency p50 / p95</span>
        <span class="ex-tile-value num">{fmtMs(stats.p50ResolveLatencyMs)}</span>
        <span class="ex-tile-sub num">place {fmtMs(stats.p50PlaceLatencyMs)}/{fmtMs(stats.p95PlaceLatencyMs)} · resolve p95 {fmtMs(stats.p95ResolveLatencyMs)}</span>
      </div>
      <div class="ex-tile">
        <span class="ex-tile-label">Total fees (real)</span>
        <!-- feeSamples 0 = no row carried a fee — an absence, never a measured $0 (AI-review MINOR 6). -->
        <span class="ex-tile-value num">{stats.feeSamples > 0 ? formatNum(stats.totalFees, 2) : '—'}</span>
        <span class="ex-tile-sub">venue fees, never simulated · n={stats.feeSamples ?? 0}</span>
      </div>
    </div>

    <SlippageChart {points} modeledBps={modeled} />

    {#if histogram.length > 0}
      <div class="ex-hist" aria-label="Slippage distribution">
        <h4 class="ex-panel-title">Slippage distribution</h4>
        <div class="ex-hist-rows">
          {#each histogram as b, i (i)}
            <div class="ex-hist-row">
              <span class="ex-hist-label num">{b.lo.toFixed(1)}–{b.hi.toFixed(1)} bps</span>
              <span class="ex-hist-track">
                <span class="ex-hist-bar" class:adverse={b.lo > 0} style={`width:${(b.count / maxBucket) * 100}%`}></span>
              </span>
              <span class="ex-hist-count num">{b.count}</span>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    {#if symbolStats.length > 1}
      <div class="ex-symbols">
        <h4 class="ex-panel-title">Per-symbol quality</h4>
        <table>
          <thead><tr><th>Symbol</th><th class="num">Orders</th><th class="num">Mean slippage</th><th class="num">Fees</th></tr></thead>
          <tbody>
            {#each symbolStats as s (s.symbol)}
              <tr>
                <td>{s.symbol.replace('_', '-')}</td>
                <td class="num">{s.count}</td>
                <td class="num">{s.meanSlippageBps != null ? formatBps(s.meanSlippageBps) : '—'}</td>
                <td class="num">{formatNum(s.totalFees, 2)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    <details class="ex-raw">
      <summary>All terminal orders ({rows.length})</summary>
      <div class="ex-raw-scroll">
        <table>
          <thead>
            <tr>
              <th>Bar time</th><th>Symbol</th><th>Side</th><th>Status</th><th class="num">Expected</th>
              <th class="num">Fill</th><th class="num">Qty</th><th class="num">Slippage</th><th class="num">Fees</th><th>Venue</th>
            </tr>
          </thead>
          <tbody>
            {#each rows as r (r.id)}
              <tr>
                <td class="num">{fmtTime(r.barTime)}</td>
                <td>{r.symbol.replace('_', '-')}</td>
                <td class="ex-side-{r.side}">{r.side}</td>
                <td>{r.status}</td>
                <td class="num">{r.expectedPrice != null ? formatNum(r.expectedPrice) : '—'}</td>
                <td class="num">{r.fillPrice != null ? formatNum(r.fillPrice) : '—'}</td>
                <td class="num">{r.fillQty != null ? formatNum(r.fillQty, 8) : '—'}</td>
                <td class="num">{formatBps(r.slippageBps)}</td>
                <td class="num">{r.fees != null ? formatNum(r.fees) : '—'}</td>
                <td>{r.venue}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </details>
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .ex-root { display: flex; flex-direction: column; gap: 0.85rem; }
  .ex-toolbar { display: flex; align-items: center; gap: 0.75rem; }
  .ex-live { font-size: 0.75rem; color: var(--success, #1aa260); }
  .ex-panel-title { margin: 0 0 0.5rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }

  .ex-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.6rem; }
  .ex-tile {
    display: flex; flex-direction: column; gap: 0.15rem;
    border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.6rem 0.75rem;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
  }
  .ex-tile-warn { border-color: var(--warning, #d8a000); }
  .ex-tile-label { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  .ex-tile-value { font-size: 1.05rem; font-weight: 700; color: var(--text-primary, #e8e9ea); font-variant-numeric: tabular-nums; }
  .ex-tile-sub { font-size: 0.68rem; color: var(--text-muted, #8a8b8d); }
  .num { font-variant-numeric: tabular-nums; }

  .ex-hist { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .ex-hist-rows { display: flex; flex-direction: column; gap: 0.22rem; }
  .ex-hist-row { display: grid; grid-template-columns: 120px minmax(0, 1fr) 3rem; align-items: center; gap: 0.6rem; font-size: 0.72rem; color: var(--text-muted, #8a8b8d); }
  .ex-hist-label { text-align: right; }
  .ex-hist-track { height: 10px; background: var(--surface-2, #14151a); border-radius: 3px; overflow: hidden; }
  .ex-hist-bar { display: block; height: 100%; border-radius: 3px; background: var(--success, #1aa260); }
  .ex-hist-bar.adverse { background: var(--danger, #e0533a); }
  .ex-hist-count { text-align: right; color: var(--text-primary, #e8e9ea); }

  .ex-symbols { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.75rem; }
  .ex-symbols table, .ex-raw table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  .ex-symbols th, .ex-symbols td, .ex-raw th, .ex-raw td {
    padding: 0.32rem 0.5rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; text-align: left;
  }
  .ex-symbols th.num, .ex-symbols td.num, .ex-raw th.num, .ex-raw td.num { text-align: right; }
  .ex-raw { border: 1px solid var(--border-subtle, #1e1f22); border-radius: 8px; padding: 0.5rem 0.75rem; }
  .ex-raw summary { cursor: pointer; font-size: 0.78rem; color: var(--text-secondary, #b5b6b8); }
  .ex-raw-scroll { max-height: 360px; overflow: auto; margin-top: 0.5rem; }
  .ex-raw th { position: sticky; top: 0; background: var(--surface-1, #14151a); }
  .ex-side-buy { color: var(--success, #1aa260); font-weight: 700; }
  .ex-side-sell { color: var(--danger, #e0533a); font-weight: 700; }
</style>
